/**
 * Diagnóstico de ChunkLoadError
 *
 * Responde a pergunta que a telemetria de hoje NÃO responde: *por que* o chunk
 * lazy falhou. Sem isto, todo ChunkLoadError chega ao Athena como uma causa só,
 * e as três possíveis pedem curas opostas.
 *
 * O runtime do webpack já sabe a resposta e a joga fora. Em
 * `__webpack_require__.f.j` ele monta o erro assim:
 *
 *     var p = c && ("load" === c.type ? "missing" : c.type);
 *     i.message = "Loading chunk " + d + " failed.\n(" + p + ": " + f + ")";
 *     i.name = "ChunkLoadError"; i.type = p; i.request = f;
 *
 * Ou seja, `type` e `request` viajam como PROPRIEDADES do objeto de erro — e o
 * handler só lia `name`/`message`/`stack`. Pior: a allowlist redige `message`
 * para `str(N)`, então o único lugar onde a causa aparecia era apagado antes de
 * sair do device.
 *
 * Os três tipos, e por que a distinção decide o roadmap:
 *  - `error`   → `onerror` do `<script>`: o script NÃO executou. Cura = retry
 *    quando é rede; ver a ressalva abaixo, porque não é só rede.
 *  - `timeout` → estourou os 120s do loader. Cura = retry (com backoff menor).
 *  - `missing` → o script disparou **`load`**, isto é, BAIXOU COM SUCESSO, e o
 *    chunk não se registrou. Conteúdo errado chegando com HTTP 200 — a
 *    assinatura do SPA fallback antigo. Retry aqui é inútil.
 *
 * **Ressalva medida em 08/09/2026: `error` NÃO significa rede.** O chunk é um
 * `<script type="module">`, e módulo tem checagem estrita de MIME: o **410
 * `text/plain` vazio** que a borda devolve para chunk podado chega ao runtime
 * como `error`, igualzinho a uma queda de conexão. Na semana de 01–08/09, dos
 * `ChunkLoadError` das builds com este diagnóstico, ~70% pediam chunk de build
 * com mais de 7 dias (410 na borda) e só ~30% pediam chunk que existia. O que
 * separa os dois é a IDADE da build (`?release=` da `chunk_url` contra o `ts`
 * do evento) — o `chunk_type` sozinho não separa.
 *
 * Os campos de `PerformanceResourceTiming` ajudam onde existem (Chromium; o
 * WebKit não cria entry para carga que falhou): 410 direto aparece com
 * `res_transfer_size` ≈ 300 (só cabeçalhos) e corpo 0; sob service worker a
 * mesma resposta aparece como 0/0.
 *
 * ## Como o DOCUMENTO velho chegou à tela — a pergunta de 08/09/2026
 *
 * Nos erros de build velha, o documento veio de cache (`doc_transfer_size = 0`)
 * sob um worker RECENTE, que não guarda documento desde 16/08. E o `index.html`
 * é servido com `no-cache`. Só dois caminhos reusam um documento assim sem
 * revalidar: navegação de HISTÓRICO (voltar/avançar, bfcache) e RESTAURAÇÃO de
 * aba. `nav_type` (`navigate`/`reload`/`back_forward`), `delivery_type`
 * (`cache` quando o Chromium serviu do cache HTTP), `page_age_s` (há quanto
 * tempo este documento existe), `susp_ms` (quanto desse tempo ele passou
 * CONGELADO) e `visible` (havia alguém olhando?) são os campos que respondem
 * isso.
 *
 * ⚠️ `page_age_s` sozinho NÃO separa "aba viva há dias" de "documento
 * restaurado agora" — este arquivo afirmava que sim até 09/09/2026, e estava
 * errado. Restaurar do bfcache devolve o MESMO documento, com o mesmo
 * `timeOrigin`, então os dois casos dão idade alta. O que separa é o tempo
 * congelado (`susp_ms`), porque o relógio monotônico para na suspensão e o de
 * parede não.
 *
 * ## Origem do DOCUMENTO — a pergunta que os campos acima não respondem
 *
 * Saber por que o chunk falhou não diz de onde veio a BUILD que o pediu. E a
 * medição de 09/08/2026 mostrou que essa é a distinção que importa: os erros de
 * boot (`/app/`) vêm de builds com idade mediana de **245 h ≈ 10 dias**, contra
 * **23 min** nos erros de pagamento. Dez dias é tempo demais para qualquer
 * cache HTTP do index (`max-age=1800`) — só o `servicePwaWorker.js` segura um
 * documento tanto tempo, porque guarda sob um `CACHE_NAME` constante e sem
 * expiração e o serve no `.catch()` quando a rede falha no boot.
 *
 * Isso era **inferência**, não medida. `doc_from_sw` e `doc_transfer_size` a
 * tornam medida: `workerStart > 0` diz que o service worker atendeu a navegação
 * e `transferSize === 0` diz que o documento não veio pela rede. Os dois juntos,
 * com uma build velha, são a prova direta do "index fóssil" — e são o gate que
 * decide se a cura é versionar o cache do service worker ou outra coisa.
 *
 * Este módulo é medição, não decisão — e por isso a regra é dura: **nunca
 * lança e nunca depende de API existir**. Ele roda dentro do ErrorHandler
 * global, onde uma exceção viraria o segundo erro. Toda leitura é defensiva e
 * todo campo é opcional: um browser que não suporta `navigator.connection` (o
 * caso do Safari/WKWebView, justamente a população mais afetada) simplesmente
 * omite o campo em vez de derrubar o diagnóstico inteiro.
 */

import { retentativasDe } from './chunk-retry';
import { estadoDoCacheDoSw, EstadoCacheSw } from './sw-cache-probe';

/**
 * Campos acrescentados ao `ClientAppError` quando — e somente quando — o erro é
 * de carregamento de chunk. Todos opcionais: o payload carrega o que o device
 * conseguiu observar, e a ausência também é sinal.
 */
export interface DiagnosticoChunk extends EstadoCacheSw {
  /** `error` | `timeout` | `missing` — direto do runtime do webpack. */
  chunk_type?: string;
  /** URL do chunk que falhou (`err.request`). Bundle estático, sem PII. */
  chunk_url?: string;
  /** `navigator.connection.effectiveType`: `4g` | `3g` | `2g` | `slow-2g`. */
  net_type?: string;
  /** Banda estimada em Mbps no instante da falha. */
  net_downlink?: number;
  /** Bytes do corpo (`encodedBodySize`). Pequeno demais ⇒ veio HTML, não o chunk. */
  res_body_size?: number;
  /** Bytes na rede (`transferSize`). `0` ⇒ veio do cache ou nunca chegou. */
  res_transfer_size?: number;
  /** Duração da request em ms. Alta ⇒ candidato a `timeout`. */
  res_ms?: number;
  /** Havia service worker no comando da página no instante da falha. */
  sw_controlled?: boolean;
  /** O service worker atendeu a NAVEGAÇÃO (`workerStart > 0`). */
  doc_from_sw?: boolean;
  /** Bytes de rede do próprio documento. `0` ⇒ veio de cache, não da rede. */
  doc_transfer_size?: number;
  /**
   * Como o documento foi navegado: `navigate` | `reload` | `back_forward` |
   * `prerender` (Navigation Timing L2). `back_forward` é a assinatura de
   * histórico/bfcache e de aba restaurada — o único caminho que reusa um
   * `index.html` servido com `no-cache` sem revalidar.
   */
  nav_type?: string;
  /** `cache` quando o Chromium serviu o documento do cache HTTP (`deliveryType`). */
  delivery_type?: string;
  /**
   * Quantas vezes ESTE documento voltou do bfcache (`pageshow.persisted`).
   *
   * Sem ele, `nav_type`, `delivery_type` e `doc_transfer_size` são armadilhas:
   * restaurar do bfcache NÃO cria entry de navegação nova — a entry fica
   * congelada na carga original, então esses três campos podem descrever uma
   * navegação de dias atrás enquanto o erro é de agora. Medido em bancada
   * (09/09/2026, Chrome 149): na volta do bfcache o estado do bundle sobrevive
   * e o `timeOrigin` não muda.
   *
   * A leitura, então, é: campo AUSENTE ou `0` ⇒ os campos de navegação valem e
   * descrevem a carga que está rodando; `>= 1` ⇒ eles descrevem outra coisa e
   * a idade real do documento é `page_age_s`, não a navegação.
   *
   * Isto também separa as duas curas que hoje se confundem: documento
   * restaurado com o bundle VIVO não pode dar erro de boot (o `main.ts` não
   * roda de novo), então `doc_restaurado >= 1` num erro de chunk significa
   * falha em rota lazy DEPOIS da volta — outro fenômeno, outra cura.
   *
   * Depende de `observarRestauracaoDeDocumento()` ter sido chamado na carga
   * original. Ausente quando não foi, e ausência nunca vira `0`.
   */
  doc_restaurado?: number;
  /**
   * Idade do documento em segundos (`Date.now() - performance.timeOrigin`).
   *
   * ⚠️ Ele NÃO distingue "aba aberta há dias" de "documento restaurado agora":
   * a restauração (bfcache/back-forward) preserva o MESMO documento, logo
   * preserva o `timeOrigin` — os dois casos dão idade alta. Quem separa é
   * `susp_ms`.
   */
  page_age_s?: number;
  /**
   * Milissegundos em que este documento ficou CONGELADO.
   *
   * `performance.now()` é monotônico e para de avançar quando o host suspende
   * o documento; `Date.now() - timeOrigin` é relógio de parede e continua. A
   * diferença é o tempo parado. Em documento vivo os dois medem a mesma coisa
   * e o campo fica AUSENTE — só viaja acima de `LIMIAR_SUSPENSAO_MS`, senão o
   * jitter de relógio (precisão reduzida de `performance.now()`, ajuste de NTP)
   * viraria "suspensão" na frota inteira.
   *
   * ⚠️ CAMPO PAREADO com o `susp_ms` da sonda de boot do `index.html`, de
   * propósito: mesmo nome, mesmo cálculo, mesmo piso, mesma regra de ausência.
   * Os dois medem populações que NÃO se cruzam por construção — aqui só entra
   * quem executou o bundle; lá, quem não executou. Ter a mesma grandeza nos
   * dois lados é o que permite perguntar se o host congelando documento é um
   * fenômeno só, visto por dois instrumentos.
   *
   * **Renomear este campo, mudar o cálculo ou mexer no piso de um lado só
   * quebra esse cruzamento EM SILÊNCIO** — cada série continua fazendo sentido
   * sozinha, e ninguém percebe que elas deixaram de ser comparáveis. Mudou de
   * um lado, muda dos dois.
   */
  susp_ms?: number;
  /** A aba estava visível no instante da falha (`document.visibilityState`). */
  visible?: boolean;
  /** `standalone` (PWA instalado) | `minimal-ui` | `fullscreen` | `browser`. */
  display_mode?: string;
  /**
   * Retentativas gastas antes deste erro (ver `chunk-retry.ts`). O campo é
   * AUSENTE quando o patch não instalou — é assim que se distingue "retentou e
   * não adiantou" (`retry_n = 2`) de "a retentativa nem está no ar" (sem campo),
   * duas leituras que dariam o mesmo número de erros e pedem decisões opostas.
   */
  retry_n?: number;
}

/** Modos de exibição que indicam app instalado, na ordem de especificidade. */
const MODOS_INSTALADO = ['standalone', 'minimal-ui', 'fullscreen'];

/** Teto de caracteres da URL no payload — o nome do chunk cabe folgado. */
const MAX_URL = 200;

/**
 * Piso para considerar que o documento ficou congelado. Abaixo disto a
 * diferença entre relógio monotônico e de parede é ruído: `performance.now()`
 * tem precisão deliberadamente reduzida e `Date.now()` sofre ajuste de NTP.
 * Emitir sem piso transformaria jitter em "suspensão" na frota inteira.
 */
export const LIMIAR_SUSPENSAO_MS = 1000;

/**
 * Lê um número só se for número de verdade. `NaN`/`Infinity` viajariam como
 * `null` no JSON e poluiriam a média no Athena — melhor campo ausente.
 */
function numeroFinito(v: any): number | null {
  return (typeof v === 'number' && isFinite(v)) ? v : null;
}

/**
 * Quantas voltas do bfcache este documento já teve. `null` = ninguém está
 * observando, e nesse caso o campo não viaja — ausência não vira zero, porque
 * "não observei" e "observei e não houve" pedem leituras diferentes.
 */
let restauracoesDoDocumento: number | null = null;

/** O que desfaz a observação; existe só enquanto ela está de pé. */
let cancelarObservacao: (() => void) | null = null;

/**
 * Passa a contar as voltas do bfcache deste documento.
 *
 * Precisa ser chamado na carga ORIGINAL, porque o `pageshow` que interessa é o
 * da volta — quem registra depois já perdeu o evento. O lugar natural é o
 * construtor do `ErrorHandler`, que o Angular instancia no bootstrap.
 *
 * Idempotente: chamar de novo devolve o mesmo cancelador sem registrar um
 * segundo ouvinte. Nunca lança.
 *
 * Devolve `null` quando NÃO conseguiu registrar (motor sem `addEventListener`,
 * ambiente sem `window`). O retorno é o controle positivo: um harness cujo
 * duplo de `window` engula `addEventListener` faria um teste passar com o
 * código tendo parado de registrar, e o valor de retorno é o que impede isso.
 */
export function observarRestauracaoDeDocumento(alvo?: any): (() => void) | null {
  if (cancelarObservacao) return cancelarObservacao;
  try {
    const janela = alvo || (typeof window !== 'undefined' ? window : null);
    if (!janela || typeof janela.addEventListener !== 'function') return null;

    const aoMostrar = (ev: any) => {
      // `persisted` só é `true` na volta do bfcache. O `pageshow` da carga
      // normal também dispara, com `false`, e não conta.
      if (ev && ev.persisted) {
        restauracoesDoDocumento = (restauracoesDoDocumento || 0) + 1;
      }
    };

    janela.addEventListener('pageshow', aoMostrar);
    restauracoesDoDocumento = 0;
    cancelarObservacao = () => {
      try {
        if (typeof janela.removeEventListener === 'function') {
          janela.removeEventListener('pageshow', aoMostrar);
        }
      } catch (e) { /* nada a fazer; o estado volta a "não observando" */ }
      restauracoesDoDocumento = null;
      cancelarObservacao = null;
    };
    return cancelarObservacao;
  } catch (e) {
    return null;
  }
}

/**
 * Extrai o que o device sabe sobre a falha de chunk. Nunca lança; devolve `{}`
 * quando não há nada a dizer.
 *
 * Recebe o erro JÁ DESEMBRULHADO — o `type`/`request` moram no erro real, e o
 * embrulho `Uncaught (in promise)` do Angular não os carrega.
 */
export function diagnosticarChunkLoad(err: any): DiagnosticoChunk {
  const d: DiagnosticoChunk = {};
  let urlCompleta = '';

  // 1) O que o próprio runtime do webpack carimbou no erro.
  try {
    const tipo = err && err.type;
    if (typeof tipo === 'string' && tipo) {
      d.chunk_type = tipo.slice(0, 20);
    }
    const req = err && err.request;
    if (typeof req === 'string' && req) {
      urlCompleta = req;
      d.chunk_url = req.slice(0, MAX_URL);
      // Quantas retentativas este chunk já gastou. Só existe se o patch de
      // `chunk-retry.ts` instalou — a ausência do campo é o canário disso.
      const n = retentativasDe(req);
      if (n !== null) d.retry_n = n;
    }
  } catch (e) { /* erro exótico (Proxy, getter que lança): segue sem os campos */ }

  // 2) Condição de rede no instante da falha. A API é Chromium-only — no
  //    WebKit os campos ficam ausentes, e essa ausência já é informação (dá
  //    para cruzar com o user_agent no Athena).
  try {
    const conexao = (navigator as any).connection;
    if (conexao) {
      if (typeof conexao.effectiveType === 'string' && conexao.effectiveType) {
        d.net_type = conexao.effectiveType.slice(0, 10);
      }
      const downlink = numeroFinito(conexao.downlink);
      if (downlink !== null) d.net_downlink = downlink;
    }
  } catch (e) { /* sem navigator.connection: campos ausentes */ }

  // 3) O que a request realmente trouxe. É isto que separa "não chegou" de
  //    "chegou a coisa errada" — e vale mesmo quando o `type` não veio.
  //    Busca pela URL COMPLETA (a entry é indexada pela URL pedida, com o
  //    `?release=` que o cache_bust anexa), não pela versão truncada.
  try {
    if (urlCompleta && typeof performance !== 'undefined' && performance.getEntriesByName) {
      const entries = performance.getEntriesByName(urlCompleta);
      // A última: se houve retry/reload, a entry recente é a que interessa.
      const entry: any = (entries && entries.length) ? entries[entries.length - 1] : null;
      if (entry) {
        const corpo = numeroFinito(entry.encodedBodySize);
        if (corpo !== null) d.res_body_size = Math.round(corpo);
        const rede = numeroFinito(entry.transferSize);
        if (rede !== null) d.res_transfer_size = Math.round(rede);
        const ms = numeroFinito(entry.duration);
        if (ms !== null) d.res_ms = Math.round(ms);
      }
    }
  } catch (e) { /* Performance API indisponível//restrita: campos ausentes */ }

  // 4) De onde veio o DOCUMENTO. Um chunk de uma build de dez dias atrás só
  //    pode ter sido pedido por um index de dez dias atrás — e o único cache
  //    capaz de guardá-lo tanto tempo é o do service worker. Aqui isso deixa
  //    de ser dedução: `doc_from_sw` diz quem atendeu a navegação e
  //    `doc_transfer_size = 0` diz que ela não passou pela rede.
  try {
    const sw = (navigator as any).serviceWorker;
    if (sw) d.sw_controlled = !!sw.controller;
  } catch (e) { /* contexto sem serviceWorker (http, webview restrita) */ }

  // 4b) QUAL service worker é esse. `sw_controlled` diz que HÁ um worker no
  //     comando; não diz se é o worker novo. E a distinção é a que faltava:
  //     medido em 15/08/2026, 128 devices rodaram a build v5 num dia e erraram
  //     no boot com a v4 num dia POSTERIOR — carregar o bundle novo pela rede
  //     não troca o worker que está registrado. O nome do cache do PWA carrega
  //     a release do worker que instalou, e o nome LEGADO (sem sufixo) é a
  //     assinatura do worker anterior à Etapa 1, cujo `activate` nunca podou
  //     nada ali. Já foi sondado no boot; aqui só se lê. Ver sw-cache-probe.ts.
  try {
    const cache = estadoDoCacheDoSw();
    if (cache.sw_cache_legacy !== undefined) d.sw_cache_legacy = cache.sw_cache_legacy;
    if (cache.sw_cache_release !== undefined) d.sw_cache_release = cache.sw_cache_release;
    if (cache.sw_cache_n !== undefined) d.sw_cache_n = cache.sw_cache_n;
  } catch (e) { /* sonda não concluiu: campos ausentes, nunca inventados */ }

  try {
    if (typeof performance !== 'undefined' && performance.getEntriesByType) {
      const navs = performance.getEntriesByType('navigation');
      // A ÚLTIMA, pela mesma razão que em `res_*` acima: se algum motor
      // acrescentar uma entry em vez de mutar a primeira, `navs[0]` passa a
      // descrever a navegação que abriu o documento e nunca a atual — e o
      // campo desenhado para detectar `back_forward` jamais diria
      // `back_forward`. Hoje a spec manda haver uma só, então isto é no-op nos
      // motores atuais; é barato e tira a única forma silenciosa de o
      // instrumento mentir. Ler os dois lados com a mesma regra também evita
      // que alguém compare `res_ms` com `doc_transfer_size` sem saber que um é
      // do fim da lista e o outro do começo.
      const nav: any = (navs && navs.length) ? navs[navs.length - 1] : null;
      if (nav) {
        // `workerStart` é 0 quando nenhum service worker interceptou. Só faz
        // sentido comparar se o campo existir — Safari antigo não o expõe.
        const inicioSw = numeroFinito(nav.workerStart);
        if (inicioSw !== null) d.doc_from_sw = inicioSw > 0;
        const rede = numeroFinito(nav.transferSize);
        if (rede !== null) d.doc_transfer_size = Math.round(rede);
        // 4c) COMO o documento chegou. Documento velho + `no-cache` no index só
        //     se explica por histórico/bfcache ou aba restaurada, e é isto que
        //     `type` diz. `deliveryType` (Chromium 130+) confirma o cache HTTP.
        if (typeof nav.type === 'string' && nav.type) d.nav_type = nav.type.slice(0, 20);
        if (typeof nav.deliveryType === 'string' && nav.deliveryType) {
          d.delivery_type = nav.deliveryType.slice(0, 30);
        }
      }
    }
  } catch (e) { /* Navigation Timing L2 ausente: campos omitidos */ }

  // 4c-bis) Quantas voltas do bfcache este documento teve. Vem junto com os
  //     campos de navegação de propósito: é ele que diz se aqueles valem.
  //     Ausente quando ninguém observou — nunca zero por omissão.
  if (restauracoesDoDocumento !== null) d.doc_restaurado = restauracoesDoDocumento;

  // 4d) HÁ QUANTO TEMPO este documento existe, e QUANTO desse tempo ele passou
  //     congelado.
  //
  //     CORREÇÃO de 09/09/2026 — o comentário anterior aqui estava ERRADO. Ele
  //     dizia que "uma aba restaurada do cache pede a mesma build com
  //     `page_age_s` de segundos", e portanto que a idade separaria os dois
  //     casos. Não separa: restaurar do bfcache preserva o MESMO documento e
  //     com ele o `timeOrigin`, então aba viva há dias e documento restaurado
  //     dão os DOIS idade alta. O campo respondia outra pergunta.
  //
  //     Quem separa é o tempo CONGELADO: `performance.now()` é monotônico e
  //     para quando o host suspende o documento, enquanto o relógio de parede
  //     continua. A diferença mede o tempo parado — perto de zero numa aba que
  //     ficou viva, grande num documento que foi congelado e devolvido.
  //
  //     São curas opostas: avisar a aba de que existe versão nova (o que
  //     `version-check.ts` já faz) × não reusar o documento. Ver a mesma
  //     medição no boot mudo em webview, onde o host encerra aos ~30 s.
  try {
    if (typeof performance !== 'undefined') {
      const origem = numeroFinito(performance.timeOrigin);
      if (origem !== null) {
        const decorridoParede = Date.now() - origem;
        const idade = Math.round(decorridoParede / 1000);
        if (isFinite(idade) && idade >= 0) d.page_age_s = idade;

        const monotonico = (typeof performance.now === 'function')
          ? numeroFinito(performance.now())
          : null;
        if (monotonico !== null) {
          const parado = Math.round(decorridoParede - monotonico);
          // Só acima do piso, e nunca negativo: relógio de parede que ANDA PARA
          // TRÁS (ajuste de NTP, usuário mexendo na data) daria negativo, que
          // não significa "suspenso ao contrário" — significa que a medida não
          // vale. Campo ausente é a resposta honesta.
          if (isFinite(parado) && parado >= LIMIAR_SUSPENSAO_MS) d.susp_ms = parado;
        }
      }
    }
  } catch (e) { /* sem timeOrigin/now (motor antigo): campos ausentes */ }

  // 4e) Havia alguém olhando? Erro em aba oculta (restauração em segundo plano,
  //     pré-carregamento) não é dano ao consumidor; contá-lo como tal infla a
  //     prioridade. Booleano quando a API existe, ausente quando não.
  try {
    if (typeof document !== 'undefined' && typeof document.visibilityState === 'string') {
      d.visible = document.visibilityState === 'visible';
    }
  } catch (e) { /* sem Page Visibility: campo ausente */ }

  // 5) App instalado na tela inicial é uma população à parte: ele quase nunca
  //    é fechado, então segura um documento em cache por muito mais tempo.
  try {
    if (typeof matchMedia === 'function') {
      for (const modo of MODOS_INSTALADO) {
        if (matchMedia('(display-mode: ' + modo + ')').matches) {
          d.display_mode = modo;
          break;
        }
      }
    }
    // iOS não implementa `display-mode`; o sinal equivalente é este.
    if (!d.display_mode && (navigator as any).standalone) d.display_mode = 'standalone';
    if (!d.display_mode) d.display_mode = 'browser';
  } catch (e) { /* sem matchMedia: campo ausente */ }

  return d;
}
